/**
 * One configured installation = no second business registration.
 * Once a local account exists (or config flag was set), login only.
 * Escape hatch for tests: REACT_APP_ALLOW_MULTI_REGISTER=true
 */

import {
  kvGet,
  kvSet,
  kvSetPersisted,
  listKvKeys
} from '../infrastructure/storage/kvAccess';
import {
  parseOfflineBusinessList,
  readAuthListFromNativeLocalStorage
} from './authCredentialRead';

/** Durable flag: survives accidental offline_businesses wipe better than list alone. */
export const INSTALLATION_CONFIGURED_KEY = 'app_installation_configured';

function envAllowsMultiRegister() {
  const v = process.env.REACT_APP_ALLOW_MULTI_REGISTER;
  return v === '1' || v === 'true';
}

function isTruthyFlag(raw) {
  return raw === true || raw === 1 || raw === '1' || raw === 'true' || raw === 'yes';
}

function offlineBusinessCount() {
  try {
    const fromKv = parseOfflineBusinessList(kvGet('offline_businesses'));
    const fromNative = readAuthListFromNativeLocalStorage('offline_businesses');
    const fromUsers = parseOfflineBusinessList(kvGet('offline_users'));
    return Math.max(fromKv.length, fromNative.length, fromUsers.length);
  } catch {
    return 0;
  }
}

function hasBusinessMachineBinding() {
  try {
    for (const key of listKvKeys()) {
      const k = String(key || '');
      // Per-business POS bind keys only (not install/browser hardware markers)
      if (!k.endsWith('_machine_id')) continue;
      if (
        k === 'app_installation_machine_id' ||
        k === 'app_stable_machine_id' ||
        k === 'browser_machine_id' ||
        k === 'electron_machine_id' ||
        k === 'machine_id_column_exists'
      ) {
        continue;
      }
      if (!k.startsWith('business_')) continue;
      const val = kvGet(k);
      if (val != null && String(val).trim() !== '') return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * Mark this installation permanently configured (after successful registration / first account).
 */
export function markInstallationConfigured() {
  try {
    kvSet(INSTALLATION_CONFIGURED_KEY, true);
    void kvSetPersisted(INSTALLATION_CONFIGURED_KEY, true);
  } catch (e) {
    console.warn('[installationRegistration] mark failed:', e?.message || e);
  }
}

/** True when this PC/browser already has a business or is marked configured. */
export function hasRegisteredBusinessOnThisInstallation() {
  if (isTruthyFlag(kvGet(INSTALLATION_CONFIGURED_KEY))) return true;
  if (offlineBusinessCount() > 0) return true;
  if (hasBusinessMachineBinding()) return true;
  return false;
}

/**
 * Registration UI + registerBusiness guard.
 * Never allowed again once configured — unless multi-register env is set.
 */
export function isRegistrationAllowedOnThisInstallation() {
  if (envAllowsMultiRegister()) return true;
  return !hasRegisteredBusinessOnThisInstallation();
}
